import React, { useEffect, useRef } from 'react';
import { LayerPanelProps } from '../types';
import './LayerPanel.css';

/**
 * Layer list and simple UV controls
 */
function LayerPanel({
  layers,
  selectedLayerId,
  onLayerSelect,
  onLayerDelete,
  onLayerReorder,
  onLayerUpdate,
  emptyTitle = 'Aucun calque',
  emptyHint = 'Ajoutez une image ou un texte'
}: LayerPanelProps) {
  const listRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!selectedLayerId || !listRef.current) return;
    const target = listRef.current.querySelector<HTMLDivElement>(
      `[data-layer-id="${selectedLayerId}"]`
    );
    if (target) {
      target.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [selectedLayerId]);

  const handleLayerClick = (layerId: string) => {
    onLayerSelect(selectedLayerId === layerId ? null : layerId);
  };

  const handleDeleteLayer = (event: React.MouseEvent, layerId: string) => {
    event.stopPropagation();
    if (window.confirm('Supprimer ce layer ?')) {
      onLayerDelete(layerId);
    }
  };

  const handleOrderChange = (layerId: string, direction: 'up' | 'down') => {
    const currentLayer = layers.find(layer => layer.id === layerId);
    if (!currentLayer) return;

    const newOrder = direction === 'up' ? currentLayer.order + 1 : currentLayer.order - 1;
    if (newOrder < 1 || newOrder > layers.length) return;
    if (newOrder === currentLayer.order) return;

    onLayerReorder(layerId, newOrder);
  };

  const getLayerIcon = (type: 'image' | 'text') => {
    const src = type === 'image'
      ? '/assets/icon/icons8-image-50.png'
      : '/assets/icon/icons8-texte-50.png';
    const alt = type === 'image' ? 'Image' : 'Texte';
    return <img src={src} alt={alt} />;
  };

  const getLayerThumbnail = (layer: any) => {
    if (layer.type === 'image') {
      const source = layer.source;
      if (source instanceof HTMLImageElement && source.src) {
        return <img src={source.src} alt={layer.data?.fileName || 'Image'} />;
      }
      if (source instanceof HTMLCanvasElement) {
        return <img src={source.toDataURL()} alt={layer.data?.fileName || 'Image'} />;
      }
    }
    return getLayerIcon(layer.type);
  };

  const getLayerDisplayName = (layer: any) => {
    if (layer.type === 'image' && layer.data?.fileName) {
      return layer.data.fileName.length > 20
        ? `${layer.data.fileName.substring(0, 17)}...`
        : layer.data.fileName;
    } else if (layer.type === 'text' && layer.data?.text) {
      return layer.data.text.length > 20
        ? `${layer.data.text.substring(0, 17)}...`
        : layer.data.text;
    }
    return `Layer ${layer.type}`;
  };

  const sortedLayers = [...layers].sort((a, b) => b.order - a.order);

  return (
    <div className="layer-panel">
      <div className="layer-panel-header">
        <h3>Calques ({layers.length})</h3>
        <div className="layer-stats">
          <span className="stat">
            <img src="/assets/icon/icons8-image-50.png" alt="Images" />
            {layers.filter(l => l.type === 'image').length}
          </span>
          <span className="stat">
            <img src="/assets/icon/icons8-texte-50.png" alt="Textes" />
            {layers.filter(l => l.type === 'text').length}
          </span>
        </div>
      </div>

      <div className="layer-list" ref={listRef}>
        {sortedLayers.length === 0 ? (
          <div className="empty-state">
            <p>{emptyTitle}</p>
            <small>{emptyHint}</small>
          </div>
        ) : (
          sortedLayers.map((layer) => (
            <div
              key={layer.id}
              className={`layer-item ${selectedLayerId === layer.id ? 'selected' : ''}`}
              data-layer-id={layer.id}
              onClick={() => handleLayerClick(layer.id)}
            >
              <div className="layer-content">
                <div className={`layer-icon ${layer.type === 'image' ? 'layer-thumb' : ''}`}>
                  {getLayerThumbnail(layer)}
                </div>

                <div className="layer-info">
                  <div className="layer-name">
                    {getLayerDisplayName(layer)}
                  </div>
                  <div className="layer-details">
                    {layer.type === 'image' && layer.data && (
                      <small>
                        {layer.data.originalSize?.width}x{layer.data.originalSize?.height}
                      </small>
                    )}
                    {layer.type === 'text' && layer.data && (
                      <small style={{ color: layer.data.color }}>
                        {layer.data.fontSize}px
                      </small>
                    )}
                  </div>
                </div>
              </div>

              <div className="layer-actions">
                <button
                  className="action-btn order-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOrderChange(layer.id, 'up');
                  }}
                  disabled={layer.order === Math.max(...layers.map(l => l.order))}
                  title="Monter"
                >
                  <img src="/assets/icon/icons8-fleche-50-Haut.png" alt="Monter" />
                </button>

                <button
                  className="action-btn order-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOrderChange(layer.id, 'down');
                  }}
                  disabled={layer.order === Math.min(...layers.map(l => l.order))}
                  title="Descendre"
                >
                  <img src="/assets/icon/icons8-fleche-50-Bas.png" alt="Descendre" />
                </button>

                <button
                  className="action-btn delete-btn"
                  onClick={(e) => handleDeleteLayer(e, layer.id)}
                  title="Supprimer"
                >
                  <img src="/assets/icon/icons8-full-trash-50.png" alt="Supprimer" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

    </div>
  );
}

export default LayerPanel;

